import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import RelatedLoadsTab from './RelatedLoadsTab.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';
import { displayPhone } from '../../phone.js';

const blank = {
  name: '', legalName: '', dateToStartWork: '', locationType: '',
  contactName: '', contactEmail: '', phone: '', extension: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada',
  tollFree: '', fax: '',
  requireCreditReport: false, requireReferenceCheck: false, requireFactoringApproval: false,
  status: 'Active', remarks: '',
  isYard: false, openingTime: '', closingTime: ''
};

function fromLocation(l) {
  if (!l) return blank;
  return {
    name: l.name || '', legalName: l.legal_name || '', dateToStartWork: l.date_to_start_work || '',
    locationType: l.location_type || '', contactName: l.contact_name || '', contactEmail: l.contact_email || '',
    phone: l.phone || '', extension: l.extension || '',
    address: l.address || '', city: l.city || '', province: l.province || '',
    postalCode: l.postal_code || '', country: l.country || 'Canada',
    tollFree: l.toll_free || '', fax: l.fax || '',
    requireCreditReport: !!l.require_credit_report, requireReferenceCheck: !!l.require_reference_check,
    requireFactoringApproval: !!l.require_factoring_approval,
    status: l.status || 'Active', remarks: l.remarks || '',
    isYard: !!l.is_yard, openingTime: l.opening_time || '', closingTime: l.closing_time || ''
  };
}

const TABS = ['Primary Info', 'Manage Contacts', 'Related Loads'];

function ContactFormModal({ locationId, locationName, contact, onClose, onSaved }) {
  const [type, setType] = useState(contact?.type || '');
  const [name, setName] = useState(contact?.name || '');
  const [phone, setPhoneVal] = useState(contact?.phone || '');
  const [extension, setExtension] = useState(contact?.extension || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [sendPickupConfirmation, setSendPickup] = useState(!!contact?.send_pickup_confirmation);
  const [sendDeliveryConfirmation, setSendDelivery] = useState(!!contact?.send_delivery_confirmation);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);


  const reset = () => {
    setType(contact?.type || ''); setName(contact?.name || ''); setPhoneVal(contact?.phone || '');
    setExtension(contact?.extension || ''); setEmail(contact?.email || '');
    setSendPickup(!!contact?.send_pickup_confirmation); setSendDelivery(!!contact?.send_delivery_confirmation);
  };

  const save = async () => {
    if (!type) { setError('Type is required.'); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    const body = { type, name, phone, extension, email, sendPickupConfirmation, sendDeliveryConfirmation };
    try {
      if (contact) {
        await api(`/dispatch/locations/${locationId}/contacts/${contact.id}`, { method: 'PATCH', body });
      } else {
        await api(`/dispatch/locations/${locationId}/contacts`, { method: 'POST', body });
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Contacts For: {locationName}</h3>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1fr 0.7fr', gap: 14, marginBottom: 14 }}>
            <label>Type <span className="req">*</span><br/>
              <select value={type} onChange={e => setType(e.target.value)}>
                <option value="">Select</option>
                <option>Billing</option>
                <option>Dispatch</option>
                <option>Operations</option>
                <option>Accounts Payable</option>
                <option>General</option>
              </select>
            </label>
            <label>Name <span className="req">*</span><br/>
              <input value={name} onChange={e => setName(e.target.value)} />
            </label>
            <label>Phone<br/>
              <PhoneInput value={phone} onChange={setPhoneVal} />
            </label>
            <label>Extn #<br/>
              <input value={extension} onChange={e => setExtension(e.target.value)} />
            </label>
          </div>
          <label style={{ display: 'block', marginBottom: 16 }}>Email ID<br/>
            <input value={email} onChange={e => setEmail(e.target.value)} />
          </label>
          <div style={{ display: 'flex', gap: 24, marginBottom: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
              <input type="checkbox" checked={sendPickupConfirmation} onChange={e => setSendPickup(e.target.checked)} style={{ width: 'auto' }} />
              Send Pickup Confirmation Email
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
              <input type="checkbox" checked={sendDeliveryConfirmation} onChange={e => setSendDelivery(e.target.checked)} style={{ width: 'auto' }} />
              Send Delivery Confirmation Email
            </label>
          </div>
          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '12px 0 0' }}>{error}</p>}
        </div>
        <div className="modal-actions" style={{ padding: '16px 24px', margin: 0, borderTop: '1px solid #e1e4e9', justifyContent: 'center' }}>
          <button type="button" className="primary-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" onClick={reset}>Reset</button>
        </div>
      </div>
    </div>
  );
}

export default function LocationFormModal({ location, allLocations = [], onClose, onSaved }) {
  const [savedId, setSavedId] = useState(location?.id || null);
  const [form, setForm] = useState(() => fromLocation(location));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nameOpen, setNameOpen] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const [contacts, setContacts] = useState(null);
  const [contactQuery, setContactQuery] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const loadContacts = () => {
    if (!savedId) return;
    setContacts(null);
    api(`/dispatch/locations/${savedId}/contacts`).then(setContacts).catch(() => setContacts([]));
  };

  useEffect(() => { if (tab === 1 && savedId) loadContacts(); }, [tab, savedId]);

  const filteredContacts = (contacts || []).filter(c => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.type, c.email, c.phone].filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));


  const nameMatches = form.name.trim()
    ? allLocations.filter(l => l.name.toLowerCase().includes(form.name.trim().toLowerCase())).slice(0, 6)
    : [];

  const pickExisting = (l) => {
    setSavedId(l.id);
    setForm(fromLocation(l));
    setNameOpen(false);
  };

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (file) setLogoPreview(URL.createObjectURL(file));
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/locations/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/locations', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { const saved = await persist(); if (saved) onSaved(saved); };
  const saveAndNext = async () => {
    if (!(await persist())) return;
    setTab(t => Math.min(t + 1, TABS.length - 1));
  };
  const saveAndNew = async () => {
    if (!(await persist())) return;
    setSavedId(null);
    setForm(blank);
    setLogoPreview(null);
    setTab(0);
  };
  const reset = () => setForm(location ? fromLocation(location) : blank);

  // Portal to <body>: this modal is also opened from inside the order form,
  // whose CSS (.create-load input { appearance: none; ... }) would otherwise
  // strip checkbox/radio styling. Rendering at body level keeps it identical
  // to how it looks in Control.
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{location?.id ? 'Edit location' : 'New location'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <label>Logo</label>
              <input id="location-logo-input" type="file" accept="image/*" onChange={handleLogoPick} style={{ display: 'none' }} />
              <label htmlFor="location-logo-input" className="logo-upload" style={{ marginBottom: 18 }}>
                {logoPreview ? <img src={logoPreview} alt="Logo preview" /> : 'Click to upload an image'}
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <label style={{ position: 'relative' }}>
                  Location Name / DBA<br/>
                  <input value={form.name}
                    onChange={e => { set('name', e.target.value); setNameOpen(true); }}
                    onFocus={() => setNameOpen(true)}
                    onBlur={() => setTimeout(() => setNameOpen(false), 150)} />
                  {nameOpen && nameMatches.length > 0 && (
                    <div className="combo-panel" style={{ top: '100%' }}>
                      <div className="combo-list">
                        {nameMatches.map(l => (
                          <div key={l.id} className="combo-option" onMouseDown={() => pickExisting(l)}>
                            {l.name}{l.city ? `, ${l.city}` : ''}{l.province ? `, ${l.province}` : ''}, {l.country || 'Canada'}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </label>
                <label>Legal Name<br/>
                  <input value={form.legalName} onChange={e => set('legalName', e.target.value)} />
                </label>
                <label>Date To Start Work<br/>
                  <input type="date" value={form.dateToStartWork} onChange={e => set('dateToStartWork', e.target.value)} />
                </label>

                <label>Location Type<br/>
                  <select value={form.locationType} onChange={e => set('locationType', e.target.value)}>
                    <option value="">—</option>
                    <option>Warehouse</option>
                    <option>Terminal</option>
                    <option>Distribution Center</option>
                    <option>Yard</option>
                    <option>Other</option>
                  </select>
                </label>
                <label>Contact<br/>
                  <input value={form.contactName} onChange={e => set('contactName', e.target.value)} />
                </label>
                <label>Email<br/>
                  <input value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
                </label>
              </div>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>

              <label className="yard-check-label">
                <input type="checkbox" checked={form.isYard} onChange={e => set('isYard', e.target.checked)} />
                Yard
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Opening Time<br/>
                  <input type="time" value={form.openingTime} onChange={e => set('openingTime', e.target.value)} />
                </label>
                <label>Closing Time<br/>
                  <input type="time" value={form.closingTime} onChange={e => set('closingTime', e.target.value)} />
                </label>
              </div>

              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }}
                onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/>
                    <input value={form.city} onChange={e => set('city', e.target.value)} />
                  </label>
                  <label>Province / State<br/>
                    <input value={form.province} onChange={e => set('province', e.target.value)} />
                  </label>
                  <label>Postal / Zip<br/>
                    <input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} />
                  </label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option>
                      <option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Phone<br/>
                  <PhoneInput value={form.phone} onChange={v => set('phone', v)} />
                </label>
                <label>Ext<br/>
                  <input value={form.extension} onChange={e => set('extension', e.target.value)} />
                </label>
                <label>Toll Free<br/>
                  <PhoneInput value={form.tollFree} onChange={v => set('tollFree', v)} />
                </label>
                <label>Fax<br/>
                  <PhoneInput value={form.fax} onChange={v => set('fax', v)} />
                </label>
              </div>

              <label>Status</label>
              <div className="status-radio-group">
                {['Active', 'Archive'].map(s => (
                  <label key={s}>
                    <input type="radio" name="location-status" checked={form.status === s}
                      onChange={() => set('status', s)} />
                    {s}
                  </label>
                ))}
              </div>

              <label>Remarks<br/>
                <textarea rows={3} value={form.remarks} onChange={e => set('remarks', e.target.value)} />
              </label>
            </>
          )}

          {tab === 1 && (
            !savedId ? (
              <div className="empty">Save the location first (try "Save & Next" from Primary Info) to add contacts.</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  <button type="button" className="primary-btn" onClick={() => { setEditingContact(null); setContactModalOpen(true); }}>
                    Add Contact
                  </button>
                </div>

                <div className="panel filter-row-panel" style={{ marginBottom: 14 }}>
                  <div className="filter-row" style={{ justifyContent: 'space-between', width: '100%' }}>
                    <input placeholder="Type to filter..." value={contactQuery}
                      onChange={e => setContactQuery(e.target.value)} style={{ width: 220 }} />
                    <label style={{ fontSize: 13, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      Show:
                      <select defaultValue={10} style={{ width: 70 }}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                      </select>
                    </label>
                  </div>
                </div>

                {contacts === null && <div className="empty">Loading...</div>}
                {contacts && (
                  <>
                    <table>
                      <thead>
                        <tr><th>Ref#</th><th>Name</th><th>Type</th><th>Email ID</th><th>Phone</th></tr>
                      </thead>
                      <tbody>
                        {filteredContacts.length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)' }}>No Data Found</td></tr>
                        )}
                        {filteredContacts.map(c => (
                          <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { setEditingContact(c); setContactModalOpen(true); }}>
                            <td>{c.ref_number || '—'}</td>
                            <td>{c.name}</td>
                            <td>{c.type || '—'}</td>
                            <td>{c.email || '—'}</td>
                            <td>{displayPhone(c.phone) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-dim)' }}>
                      Showing {filteredContacts.length === 0 ? 0 : 1} to {filteredContacts.length} of {filteredContacts.length} entries
                    </div>
                  </>
                )}
              </>
            )
          )}

          {tab === 2 && (
            !savedId
              ? <div className="empty">Save the location first to see related loads.</div>
              : <RelatedLoadsTab endpoint={`/dispatch/locations/${savedId}/loads`} />
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNew} disabled={saving}>Save &amp; New</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : 'Save & Close'}
            </button>
          </div>
        </div>
      </div>

      {contactModalOpen && savedId && (
        <ContactFormModal
          locationId={savedId}
          locationName={form.name}
          contact={editingContact}
          onClose={() => setContactModalOpen(false)}
          onSaved={() => { setContactModalOpen(false); loadContacts(); }}
        />
      )}
    </div>,
    document.body
  );
}
